import type {
  LitNodeClientNodeJs
} from '@lit-protocol/lit-node-client'
import type {
  AccessControlConditions,
  LIT_NETWORKS_KEYS,
  SessionSigsMap,
} from '@lit-protocol/types'
import { generateKeyWithLitAction } from '@lit-protocol/wrapped-keys/src/lib/lit-actions-client'
import { getLitActionCodeOrCid } from '@lit-protocol/wrapped-keys/src/lib/lit-actions-client/utils'
import { storePrivateKey } from '@lit-protocol/wrapped-keys/src/lib/service-client/client'
import type {
  GeneratePrivateKeyResult,
  KeyType,
  Network
} from '@lit-protocol/wrapped-keys/src/lib/types'
import { VAULT_SECRET_IPFS_HASHES } from '@polaris/lit-actions/src/ipfs-constants'
import { getVaultAccessControlConditions } from '@polaris/lit-actions/src/lib/internal/common/access-control-conditions'
import { LIT_ACTION_CIDS } from '../../ipfs-constants'
import { NAMADA_KEY_TYPE } from '../constants'
import {
  getFirstSessionSig,
  getPkpAccessControlCondition,
  getPkpAddressFromSessionSig,
} from '../internal/common'
import { listWrappedKeys } from './list'
import { AccessControlConditionsType, ExtendedKeyType, ExtendedNetwork } from './types'


/**
 * Gets the access control conditions by the access control conditions type.
 * See enum AccessControlConditionsType for the possible values.
 *
 * @param accessControlConditionsType - The type of access control conditions to get
 * @param pkpAddress - The PKP address to get the access control conditions for
 * @returns
 */
function getAccessControlConditionsByType(
  accessControlConditionsType: AccessControlConditionsType,
  pkpAddress: string,
): AccessControlConditions {
  switch (accessControlConditionsType) {
    case AccessControlConditionsType.VAULT:
      return getVaultAccessControlConditions(
        pkpAddress,
        VAULT_SECRET_IPFS_HASHES,
      )
    case AccessControlConditionsType.PKP_SCOPED:
      return [getPkpAccessControlCondition(pkpAddress)]
    default:
      throw new Error(
        `Invalid access control conditions type: ${accessControlConditionsType}`,
      )
  }
}

/**
 * Generates a wrapped key for a given key type.
 * Access control conditions are defined by the access control conditions type.
 * This can be either a VAULT or a RUNNER.
 *
 * @param litNodeClient
 * @param wallet
 * @param pkpSessionSigsOrPublicKey
 * @param keyType
 * @param memo
 * @param accessControlConditionsType
 * @param rootKeyId
 * @returns
 */
export async function generateWrappedKey(
  litNodeClient: LitNodeClientNodeJs,
  pkpSessionSigs: SessionSigsMap,
  keyType: ExtendedKeyType,
  memo: string,
  accessControlConditionsType: AccessControlConditionsType = AccessControlConditionsType.PKP_SCOPED,
  rootKeyId?: string,
): Promise<GeneratePrivateKeyResult> {

  // We do not want to generate duplicate keys
  const existingKeys = await listWrappedKeys(
    litNodeClient,
    pkpSessionSigs,
  )

  if (
    existingKeys.some((key) => key.keyType === keyType && key.memo === memo)
  ) {
    throw new Error(
      `Key with type ${keyType} and memo '${memo}' already exists`,
    )
  }

  return generateKey(
    litNodeClient,
    pkpSessionSigs,
    keyType,
    memo,
    accessControlConditionsType,
    rootKeyId,
  ).catch((error) => {
    throw new Error(error.message)
  })
}

/**
 * Generates a wrapped key for a given key type
 * @param litNodeClient - The LitNodeClient instance
 * @param pkpSessionSigs - The PKP sessionSigs (map) used to associate the PKP with the generated private key
 * @param keyType - The type of key to generate
 * @param memo - The memo for the key
 * @param rootKeyId - Optional root key ID (required for Namada keys)
 * @returns The generated wrapped key
 */
async function generateKey(
  litNodeClient: LitNodeClientNodeJs,
  pkpSessionSigs: SessionSigsMap,
  keyType: ExtendedKeyType,
  memo: string,
  accessControlConditionsType: AccessControlConditionsType = AccessControlConditionsType.PKP_SCOPED,
  rootKeyId?: string,
) {
  const networkByKeyType: Record<ExtendedKeyType, ExtendedNetwork> = {
    K256: 'evm',
    ed25519: 'solana',
    [NAMADA_KEY_TYPE]: 'namada',
  }

  switch (keyType) {
    case NAMADA_KEY_TYPE:
      if (!rootKeyId) {
        throw new Error('rootKeyId is required for Namada key generation')
      }
      if (accessControlConditionsType !== AccessControlConditionsType.VAULT) {
        throw new Error(
          'Access control conditions type must be VAULT for Namada key generation',
        )
      }
      return generateNamadaKey(litNodeClient, pkpSessionSigs, memo, rootKeyId)
    default: {
      return await generateAndStoreWrappedKey(
        litNodeClient,
        pkpSessionSigs,
        keyType,
        memo,
        networkByKeyType[keyType] as Network,
        accessControlConditionsType,
      )
    }
  }
}

/**
 * Generates and stores a wrapped key for a given key type
 * We call a Lit maintained Lit Action to generate the wrapped key.
 * This is only used for K256 and ed25519 key types.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param keyType
 * @param memo
 * @param network
 * @param accessControlConditionsType
 * @returns
 */
async function generateAndStoreWrappedKey(
  litNodeClient: LitNodeClientNodeJs,
  pkpSessionSigs: SessionSigsMap,
  keyType: ExtendedKeyType,
  memo: string,
  network: Network,
  accessControlConditionsType: AccessControlConditionsType,
) {
  // Get the Lit Action code and IPFS CID for the generateEncryptedKey Lit Action
  const { litActionCode, litActionIpfsCid } = getLitActionCodeOrCid(
    network,
    'generateEncryptedKey',
  )

  // Determine the access control conditions based on the access control conditions type
  const accessControlConditions = getAccessControlConditionsByType(
    accessControlConditionsType,
    getPkpAddressFromSessionSig(getFirstSessionSig(pkpSessionSigs)),
  )

  // Generate the wrapped key using the Lit Action
  const { ciphertext, dataToEncryptHash, publicKey } =
    await generateKeyWithLitAction({
      litNodeClient,
      litActionIpfsCid: litActionCode ? undefined : litActionIpfsCid,
      litActionCode: litActionCode ? litActionCode : undefined,
      // There's a typing issue here as it seems like the same type used in the wrapped keys package
      // is not the same as the type used in the lit-protocol/types package in our node_modules
      // biome-ignore lint/suspicious/noExplicitAny: TODO: fix our dependencies to ensure these types are consistent
      accessControlConditions: accessControlConditions as any,
      memo,
      network,
      pkpAddress: getPkpAddressFromSessionSig(getFirstSessionSig(pkpSessionSigs)),
      pkpSessionSigs,
    })

  // Store the wrapped key in Lit's wrapped key service
  const { id, pkpAddress } = await storePrivateKey({
    litNetwork: litNodeClient.config.litNetwork as LIT_NETWORKS_KEYS,
    sessionSig: getFirstSessionSig(pkpSessionSigs),
    storedKeyMetadata: {
      ciphertext,
      publicKey,
      keyType: keyType as KeyType,
      dataToEncryptHash,
      memo,
    },
  })

  return { id, pkpAddress, generatedPublicKey: publicKey, keyType }
}

/**
 * Generates a Namada key using a Lit Action
 * A Namada key is stored as a bip39 seed phrase derived from a root key
 * @param litNodeClient - The LitNodeClient instance
 * @param pkpSessionSigs - The PKP sessionSigs (map) used to associate the PKP with the generated private key
 * @param memo - The memo for the key
 * @param rootKeyId - The ID of the root key to derive from
 * @returns The generated wrapped key
 */
async function generateNamadaKey(
  litNodeClient: LitNodeClientNodeJs,
  pkpSessionSigs: SessionSigsMap,
  memo: string,
  rootKeyId: string,
) {
  const pkpAddress = getPkpAddressFromSessionSig(getFirstSessionSig(pkpSessionSigs))

  // Execute the key from root generation Lit Action with all required parameters
  const result = await litNodeClient.executeJs({
    sessionSigs: pkpSessionSigs,
    ipfsId: LIT_ACTION_CIDS.generateNamadaKeySeedFromRoot,
    jsParams: {
      rootKeyId,
      pkpAddress,
      litNetwork: litNodeClient.config.litNetwork,
      sessionSig: getFirstSessionSig(pkpSessionSigs),
      // This is used to scope the access control conditions for the vault secret IPFS hashes
      // This internally calls `getVaultAccessControlConditions` to get the access control conditions using these IPFS hashes
      // They MUST match the IPFS hashes used in the VAULT_SECRET_IPFS_HASHES constant and what the root key is encrypted with.
      ipfsHashes: VAULT_SECRET_IPFS_HASHES,
    },
  })

  // Validate the result
  if (!result?.success || !result?.response) {
    throw new Error(`Lit Action execution failed: ${JSON.stringify(result)}`)
  }

  if (
    typeof result.response === 'string' &&
    result.response.toLowerCase().includes('error')
  ) {
    throw new Error(`Error executing the Lit Action: ${result.response}`)
  }

  // Parse the response
  const { ciphertext, dataToEncryptHash, publicKey } = JSON.parse(
    result.response as string,
  )

  const { id } = await storePrivateKey({
    sessionSig: getFirstSessionSig(pkpSessionSigs),
    storedKeyMetadata: {
      ciphertext,
      publicKey,
      keyType: 'ed25519',
      dataToEncryptHash,
      memo,
    },
    litNetwork: litNodeClient.config.litNetwork,
  })

  return {
    id,
    pkpAddress,
    generatedPublicKey: publicKey,
    keyType: NAMADA_KEY_TYPE,
  }
}
